<template>
  <div>
    <!-- Carnival Inquiries Card -->
    <b-card class="resources-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-0 mt-3 mx-1">
        <div>
          <h5 class="mb-0 resource-title">Carnival Inquiries</h5>
          <p class="text-muted mb-0 font-small-3">Manage and review carnival inquiries</p>
        </div>

        <!-- View Toggle Buttons -->
        <div class="view-toggle-wrapper">
          <div class="custom-toggle-group shadow-sm">
            <button 
              class="toggle-btn" 
              :class="{ active: viewMode === 'graphical' }"
              @click="toggleView('graphical')"
            >
              <feather-icon icon="BarChart2Icon" size="14" class="mr-50" />
              <span>Graphical</span>
            </button>
            <button 
              class="toggle-btn" 
              :class="{ active: viewMode === 'table' }"
              @click="toggleView('table')"
            >
              <feather-icon icon="TableIcon" size="14" class="mr-50" />
              <span>Table Data</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Dashboard Section (Graphical View) -->
      <div v-if="viewMode === 'graphical'" class="dashboard-section pt-2 pb-2">
        <!-- Metric Cards -->
        <b-row class="mb-2">
          <template v-if="dataLoading">
            <b-col v-for="i in 5" :key="'metric-skeleton-'+i" xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card skeleton-pulse shadow-sm" style="background: #f8fafc; border: 1px solid #e2e8f0;">
                <div class="metric-icon-wrapper skeleton-shimmer" style="background: #e2e8f0; border: none;"></div>
                <div class="metric-content" style="width: 60%">
                  <div class="skeleton-shimmer mb-50" style="height: 10px; width: 80%; border-radius: 4px;"></div>
                  <div class="skeleton-shimmer" style="height: 20px; width: 50%; border-radius: 4px;"></div>
                </div>
              </div>
            </b-col>
          </template>
          <template v-else>
            <b-col xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card gold-gradient shadow-sm">
                <div class="metric-icon-wrapper ring-gold">
                  <feather-icon icon="UserCheckIcon" size="18" />
                </div>
                <div class="metric-content text-white">
                  <div class="metric-label">Registrations</div>
                  <div class="metric-value">{{ statsData.totalRegistrations }}</div>
                </div>
              </div>
            </b-col>
            <b-col xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card indigo-gradient shadow-sm">
                <div class="metric-icon-wrapper ring-indigo">
                  <feather-icon icon="UsersIcon" size="18" />
                </div>
                <div class="metric-content text-white">
                  <div class="metric-label">Headcount</div>
                  <div class="metric-value">{{ statsData.totalHeadcount }}</div>
                </div>
              </div>
            </b-col>
            <b-col xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card emerald-gradient shadow-sm">
                <div class="metric-icon-wrapper ring-emerald">
                  <feather-icon icon="HomeIcon" size="18" />
                </div>
                <div class="metric-content text-white">
                  <div class="metric-label">Total Schools</div>
                  <div class="metric-value">{{ statsData.schoolWise.length }}</div>
                </div>
              </div>
            </b-col>
            <b-col xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card rose-gradient shadow-sm">
                <div class="metric-icon-wrapper ring-rose">
                  <feather-icon icon="TrendingUpIcon" size="18" />
                </div>
                <div class="metric-content text-white">
                  <div class="metric-label">Avg Headcount</div>
                  <div class="metric-value">{{ statsData.totalRegistrations > 0 ? (statsData.totalHeadcount / statsData.totalRegistrations).toFixed(1) : 0 }}</div>
                </div>
              </div>
            </b-col>
            <b-col xl lg md="6" sm="6" cols="12" class="mb-1">
              <div class="metric-card amber-gradient shadow-sm">
                <div class="metric-icon-wrapper ring-amber">
                  <feather-icon icon="MapPinIcon" size="18" />
                </div>
                <div class="metric-content text-white">
                  <div class="metric-label">Total Present</div>
                  <div class="metric-value">{{ statsData.totalPresent || 0 }}</div>
                </div>
              </div>
            </b-col>
          </template>
        </b-row>

        <!-- Charts Row -->
        <b-row v-if="dataLoading">
          <b-col lg="6" class="mb-2" v-for="j in 2" :key="'chart-skel-'+j">
            <div class="chart-card shadow-sm p-2" style="height: 350px;">
              <div class="skeleton-shimmer mb-2" style="height: 20px; width: 40%; border-radius: 4px;"></div>
              <div class="skeleton-shimmer h-100" style="border-radius: 12px;"></div>
            </div>
          </b-col>
        </b-row>
        <!-- Charts Row -->
        <b-row v-else>
          <b-col lg="6" class="mb-3">
            <div class="modern-chart-card">
              <div class="chart-header-modern">
                <h6 class="chart-title-modern">Class Distribution</h6>
                <feather-icon icon="BarChart2Icon" size="18" class="text-indigo" />
              </div>
              <div class="chart-content-modern">
                <vue-apexcharts
                  type="bar"
                  height="350"
                  :options="classChartOptions"
                  :series="classChartSeries"
                />
              </div>
            </div>
          </b-col>
          <b-col lg="6" class="mb-3">
            <div class="modern-chart-card">
              <div class="chart-header-modern">
                <h6 class="chart-title-modern">School Distribution</h6>
                <feather-icon icon="PieChartIcon" size="18" class="text-indigo" />
              </div>
              <div class="chart-content-modern">
                <vue-apexcharts
                  type="donut"
                  height="350"
                  :options="schoolChartOptions"
                  :series="schoolChartSeries"
                />
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Profession Chart -->
        <b-row v-if="!dataLoading">
          <b-col lg="12" class="mb-3">
            <div class="modern-chart-card">
              <div class="chart-header-modern">
                <h6 class="chart-title-modern">Profession Distribution</h6>
                <feather-icon icon="BriefcaseIcon" size="18" class="text-indigo" />
              </div>
              <div class="chart-content-modern">
                <vue-apexcharts
                  type="bar"
                  height="350"
                  :options="professionChartOptions"
                  :series="professionChartSeries"
                />
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Missing Data Alert -->
        <div v-if="!dataLoading && missingDataCount > 0" class="quality-alert mb-2">
          <div class="alert-gradient-border"></div>
          <div class="d-flex align-items-center p-2">
            <div class="alert-icon-wrapper mr-2">
              <feather-icon icon="AlertTriangleIcon" size="24" class="text-warning" />
            </div>
            <div>
              <h6 class="mb-0 font-weight-bolder text-dark">Data Quality Alert</h6>
              <p class="mb-0 text-muted font-small-3">Some registrations ({{ missingDataCount }}) have missing schools or professions that need your attention.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Table Content Section (Table Data View) -->
      <div v-if="viewMode === 'table'" class="table-content-section mt-1">
        <!-- Filter Controls (Now Single Layer) -->
        <div class="filter-header-modern mb-3">
          <b-row class="align-items-center mx-1 py-1">
            <!-- Code Filter -->
            <b-col xl="2" lg="4" md="6" sm="12" class="my-50">
              <div class="input-modern-group">
                <feather-icon icon="HashIcon" size="16" class="input-icon" />
                <b-form-input
                  id="filterID"
                  v-model="filterID"
                  placeholder="Filter by Code"
                  class="filter-control-modern"
                  @input="onSearchChange"
                  debounce="500"
                />
              </div>
            </b-col>

            <!-- Search Input -->
            <b-col xl="3" lg="4" md="6" sm="12" class="my-50">
              <div class="input-modern-group">
                <feather-icon icon="SearchIcon" size="16" class="input-icon" />
                <b-form-input
                  id="search"
                  v-model="searchQuery"
                  placeholder="Search by name..."
                  class="filter-control-modern"
                  @input="onSearchChange"
                  debounce="500"
                />
              </div>
            </b-col>

            <!-- Class Filter -->
            <b-col xl="2" lg="4" md="6" sm="12" class="my-50">
              <div class="input-modern-group">
                <feather-icon icon="FilterIcon" size="16" class="input-icon" />
                <v-select
                  ref="fil_class"
                  v-model="filterClassID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="[{ name: 'All Classes', id: 0 }, ...classesOptions]"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  label="name"
                  class="v-select-modern"
                  append-to-body
                  @input="onSearchChange"
                  placeholder="All Classes"
                />
              </div>
            </b-col>

            <!-- Last School Filter -->
            <b-col xl="3" lg="4" md="6" sm="12" class="my-50">
              <div class="input-modern-group">
                <feather-icon icon="HomeIcon" size="16" class="input-icon" />
                <v-select
                  v-model="filterLastSchool"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="[
                    { label: 'All Schools', id: 'all' },
                    { label: 'The Elixir School', id: 'The Elixir School' },
                    { label: 'Others', id: 'other' }
                  ]"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  label="label"
                  class="v-select-modern"
                  append-to-body
                  @input="onSearchChange"
                  placeholder="Last School"
                />
              </div>
            </b-col>
            
            <b-col class="ml-auto text-right my-50">
              <b-button 
                variant="success" 
                size="sm" 
                @click="exportToExcel" 
                class="mr-1"
                :disabled="exportLoading"
              >
                <b-spinner v-if="exportLoading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
                {{ exportLoading ? 'Exporting...' : 'Export' }}
              </b-button>
             
            </b-col>
          </b-row>
        </div>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2 mt-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">SHOW:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>
        
        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalRecords }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
          class="resources-table"
          :items="inquiries"
          :fields="fields"
          :busy="dataLoading"
          :responsive="false"
          hover
          small
          show-empty
          empty-text="No carnival inquiries found"
          @row-clicked="viewInquiry"
        >
        <template #table-busy>
          <tbody>
            <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
              <td v-for="(field, index) in fields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                <div class="skeleton-loader"></div>
              </td>
            </tr>
          </tbody>
        </template>

        <template #empty>
          <h3 class="mt-1 mb-1 text-center">
            No records found
          </h3>
        </template>

        <template #head()="data">
          <span class="text-uppercase font-weight-bolder small">{{ data.label }}</span>
        </template>

        <template #cell(sno)="{ index }">
          <span class="text-dark font-weight-bold">{{ (currentPage - 1) * perPage + index + 1 }}</span>
        </template>

        <template #cell(id)="{ item }">
          <span class="text-primary font-weight-bolder">{{ item.id }}</span>
        </template>

        <template #cell(name)="{ item }">
          <div class="d-flex align-items-center">
            <div class="avatar-wrapper mr-1">
              <div class="avatar bg-light-primary">
                <feather-icon icon="UserIcon" size="14" class="text-primary" />
              </div>
            </div>
            <div class="font-weight-bold text-dark">{{ item.name || 'N/A' }}</div>
          </div>
        </template>

        <template #cell(age)="{ item }">
          <span class="text-dark font-weight-bold">{{ item.age || '0' }}</span>
        </template>

        <template #cell(contact)="{ item }">
          <div class="d-flex align-items-center">
            <feather-icon icon="PhoneIcon" size="14" class="text-muted mr-50" />
            <span class="text-dark font-weight-bold">{{ item.contact || 'N/A' }}</span>
          </div>
        </template>

        <template #cell(email)="{ item }">
          <div class="d-flex align-items-center text-secondary">
            <feather-icon icon="MailIcon" size="14" class="mr-50" />
            <span>{{ item.email || 'N/A' }}</span>
          </div>
        </template>

        <template #cell(className)="{ item }">
          <b-badge variant="light-primary" class="custom-badge-modern">
            {{ item.className || 'N/A' }}
          </b-badge>
        </template>

        <template #cell(cnic)="{ item }">
          <span class="text-dark">{{ item.cnic || 'N/A' }}</span>
        </template>

        <template #cell(lastSchool)="{ item }">
          <span class="text-dark">{{ item.lastSchool || 'N/A' }}</span>
        </template>

        <template #cell(note)="{ item }">
          <span class="text-dark font-weight-bold">{{ cleanNoteText(item.note) }}</span>
        </template>

        <template #cell(finalRemarks)="{ item }">
          <span class="text-dark">{{ item.finalRemarks || 'N/A' }}</span>
        </template>

        <template #cell(inquiryStatus)="{ item }">
          <div 
            class="status-pill"
            :class="(item.inquiryStatus || '').toLowerCase() === 'present' ? 'status-present' : 'status-pending'"
          >
            <feather-icon 
              :icon="(item.inquiryStatus || '').toLowerCase() === 'present' ? 'CheckIcon' : 'ClockIcon'" 
              size="14" 
            />
            <span>{{ (item.inquiryStatus || '').toLowerCase() === 'present' ? 'Present' : 'Pending' }}</span>
          </div>
        </template>

        <template #cell(date)="{ item }">
          <div class="d-flex align-items-center text-muted">
            <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
            <span class="font-weight-bold">{{ formatDate(item.date) }}</span>
          </div>
        </template>

        <template #head(actions)="data">
          <span class="text-uppercase font-weight-bolder small">{{ data.label }}</span>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex justify-content-center align-items-center" @click.stop>
            <b-button
              v-if="(item.inquiryStatus || '').toLowerCase() !== 'present'"
              variant="outline-success"
              size="sm"
              class="btn-icon rounded-circle mr-50"
              v-b-tooltip.hover
              title="Mark as Present"
              @click="SetPresent(item.id)"
            >
              <feather-icon icon="UserCheckIcon" size="16" />
            </b-button>

            <b-button
              v-if="rights.delete"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="outline-danger"
              v-b-tooltip.hover.right
              title="Delete"
              size="sm"
              class="btn-icon"
              @click="Delete(item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        </b-table>
      </div>
      </div> <!-- End table-scroll-container -->
    </div> <!-- End table-content-section -->
    </b-card>

    <!-- View Inquiry Sidebar -->
    <b-sidebar
      id="view-inquiry-sidebar"
      :visible="isSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header-close
      right
      @hidden="onSidebarHidden"
      @change="onSidebarChange"
      class="resource-sidebar"
    >
      <div class="sidebar-header-gradient">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <div class="sidebar-icon-wrapper">
              <feather-icon 
                icon="EyeIcon" 
                size="20" 
                class="sidebar-title-icon"
              />
            </div>
            <h5 class="mb-0 sidebar-title">View Inquiry</h5>
          </div>
          <b-button
            variant="link"
            size="sm"
            class="sidebar-close-btn-modern"
            @click="toggleSidebar(false)"
          >
            <feather-icon icon="XIcon" size="18" />
          </b-button>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <b-form @submit.prevent class="sidebar-form-modern">
          <!-- Inquiry Code -->
          <b-form-group label="Inquiry Code" class="modern-form-group">
            <div class="modern-display-text text-primary font-weight-bolder">
              #{{ currentInquiry.id }}
            </div>
          </b-form-group>

          <!-- Full Name -->
          <b-form-group label="Full Name" class="modern-form-group">
            <div class="modern-display-text text-primary font-weight-bold">
              {{ currentInquiry.name || "N/A" }}
            </div>
          </b-form-group>

          <!-- Total Head Count -->
          <b-form-group label="Total Head Count" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.age || "0" }}
            </div>
          </b-form-group>

          <!-- Contact Number -->
          <b-form-group label="Contact Number" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.contact || "N/A" }}
            </div>
          </b-form-group>

          <!-- Email -->
          <b-form-group label="Email" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.email || "N/A" }}
            </div>
          </b-form-group>

          <!-- Class -->
          <b-form-group label="Class" class="modern-form-group">
            <div class="modern-display-text">
              <b-badge variant="light-primary">{{ currentInquiry.className || "N/A" }}</b-badge>
            </div>
          </b-form-group>

          <!-- CNIC -->
          <b-form-group label="CNIC Number" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.cnic || "N/A" }}
            </div>
          </b-form-group>

          <!-- Kids studying in school name -->
          <b-form-group label="Kids studying in school name" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.lastSchool || "N/A" }}
            </div>
          </b-form-group>

          <!-- How many children -->
          <b-form-group label="How many children" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.note || "N/A" }}
            </div>
          </b-form-group>

          <!-- Profession -->
          <b-form-group label="Profession" class="modern-form-group">
            <div class="modern-display-text">
              {{ currentInquiry.finalRemarks || "N/A" }}
            </div>
          </b-form-group>

          <!-- Date -->
          <b-form-group label="Inquiry Date" class="modern-form-group">
            <div class="modern-display-text">
              {{ formatDate(currentInquiry.date) }}
            </div>
          </b-form-group>
        </b-form>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BPagination, BForm,
  BFormGroup, BFormInput, BSpinner,
  BSidebar, VBTooltip, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import VueApexcharts from 'vue-apexcharts'
import { mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as XLSX from 'xlsx'

export default {
  name: 'CarnivalInquiries',
  components: {
    BCard, BRow, BCol, BButton, BTable, BPagination, BForm,
    BFormGroup, BFormInput, BSpinner,
    BSidebar, vSelect, BBadge, VueApexcharts
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      inquiries: [],
      currentInquiry: {},
      classesOptions: [],
      dataLoading: false,
      currentPage: 1,
      perPage: 10,
      totalRecords: 0,
      searchQuery: '',
      filterClassID: null,
      filterLastSchool: 'all',
      filterID: null,
      searchTimeout: null,
      isSidebarOpen: false,
      viewMode: 'graphical', // 'graphical' or 'table'
      exportLoading: false,
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '40', value: 40 },
        { label: '80', value: 80 },
        { label: '100', value: 100 },
        { label: '200', value: 200 }
      ],
      statsData: {
        totalRegistrations: 0,
        totalHeadcount: 0,
        totalPresent: 0,
        totalChildrenCount: 0,
        classWise: [],
        schoolWise: [],
        professionWise: [],
      },
      fields: [
        { key: 'sno', label: 'S.No', sortable: false, tdClass: 'align-middle text-center', thClass: 'text-center', thStyle: 'width: 70px' },
        { key: 'id', label: 'Code', sortable: true, tdClass: 'align-middle font-weight-bold text-primary', thStyle: 'width: 100px' },
        { key: 'name', label: 'Full Name', sortable: false, tdClass: 'align-middle font-weight-bold' },
        { key: 'className', label: 'Class', sortable: false, tdClass: 'align-middle', thStyle: 'width: 120px' },
        { key: 'age', label: 'Head Count', sortable: false, tdClass: 'align-middle text-center', thClass: 'text-center', thStyle: 'width: 120px' },
        { key: 'contact', label: 'Contact', sortable: false, tdClass: 'align-middle', thStyle: 'width: 150px' },
        { key: 'inquiryStatus', label: 'Status', sortable: false, tdClass: 'align-middle text-center', thClass: 'text-center', thStyle: 'width: 120px' },
        { key: 'date', label: 'Date', sortable: false, tdClass: 'align-middle', thStyle: 'width: 150px' },
        { key: 'actions', label: 'Actions', sortable: false, tdClass: 'align-middle text-center', thClass: 'text-center', thStyle: 'width: 120px' },
      ],
    }
  },
  computed: {
    displayedRange() {
      const start = (this.currentPage - 1) * this.perPage + 1
      const count = this.inquiries ? this.inquiries.length : 0
      const end = Math.min((this.currentPage - 1) * this.perPage + count, this.totalRecords)
      return { start, end: end > 0 ? end : 0 }
    },
    missingDataCount() {
      // In a real app we'd fetch this from stats, but for UI we can estimate
      const noSchool = this.statsData.schoolWise.find(s => !s.school)?.count || 0
      const noProfession = this.statsData.professionWise.find(p => !p.profession)?.count || 0
      return Math.max(noSchool, noProfession)
    },
    classChartSeries() {
      return [{
        name: 'Inquiries',
        data: this.statsData.classWise.map(c => c.count)
      }]
    },
    classChartOptions() {
      return {
        chart: { 
          type: 'bar',
          width: '100%',
          toolbar: { show: false },
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        colors: ['#6366f1', '#10b981', '#f43f5e', '#f59e0b', '#06b6d4', '#8b5cf6', '#ec4899'],
        plotOptions: { 
          bar: { 
            borderRadius: 8, 
            columnWidth: '60%',
            distributed: true,
          } 
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: this.statsData.classWise.map(c => this.getClassName(c.clsID)),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { 
            style: { colors: '#64748b', fontSize: '12px', fontWeight: 500 },
          }
        },
        yaxis: { 
          show: false 
        },
        grid: { 
          show: true,
          borderColor: '#f1f5f9',
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: false } },
          padding: {
            top: 10,
            bottom: 10,
            left: 10,
            right: 24
          }
        },
        tooltip: {
          theme: 'light',
          style: { fontSize: '12px' },
          y: {
            formatter: (val) => val + ' students'
          }
        }
      }
    },
    schoolChartSeries() {
      return this.statsData.schoolWise.map(s => s.count)
    },
    schoolChartOptions() {
      return {
        chart: { 
          type: 'donut',
          width: '100%',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        labels: this.statsData.schoolWise.map(s => s.school || 'Unspecified'),
        colors: ['#6366f1', '#10b981', '#f43f5e', '#f59e0b', '#06b6d4', '#8b5cf6', '#ec4899', '#64748b'],
        stroke: {
          show: true,
          width: 2,
          colors: ['#fff']
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.15,
            }
          },
          active: {
            filter: {
              type: 'none'
            }
          }
        },
        legend: { 
          position: 'bottom', 
          fontSize: '13px',
          fontWeight: 500,
          labels: { colors: '#64748b' },
          markers: {
            width: 10,
            height: 10,
            radius: 12
          },
          itemMargin: { horizontal: 10, vertical: 8 }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Schools',
                  formatter: () => this.statsData.schoolWise.length,
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontWeight: 500,
                },
                value: {
                  show: true,
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#1e293b',
                  offsetY: 8,
                  formatter: (val) => val
                }
              }
            },
            expandOnClick: false
          }
        },
        dataLabels: { 
          enabled: false
        },
        tooltip: {
          theme: 'light',
          fillSeriesColor: false,
          y: {
            formatter: (val) => val + ' registrations'
          }
        }
      }
    },
    professionChartSeries() {
      return [{
        name: 'Registrations',
        data: this.statsData.professionWise.map(p => p.count)
      }]
    },
    professionChartOptions() {
      return {
        chart: {
          type: 'bar',
          height: 350,
          width: '100%',
          toolbar: { show: false },
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 8,
            barHeight: '60%',
            distributed: true,
          }
        },
        colors: ['#6366f1', '#10b981', '#f43f5e', '#f59e0b', '#06b6d4', '#8b5cf6', '#ec4899'],
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: this.statsData.professionWise.map(p => p.profession || 'Not Specified'),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { 
            style: { colors: '#94a3b8', fontSize: '12px', fontWeight: 500 },
          }
        },
        yaxis: {
          labels: {
            style: { colors: '#64748b', fontSize: '12px', fontWeight: 500 },
          }
        },
        grid: {
          show: true,
          borderColor: '#f1f5f9',
          strokeDashArray: 3,
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } },
          padding: {
            top: 0,
            bottom: 0,
            left: 10,
            right: 24
          }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: (val) => val + ' registrations'
          }
        }
      }
    },
  },
  created() {
    this.setupRights()
    
    if (!this.rights || !this.rights.view) {
      this.$router.replace({ name: 'misc-not-authorized' })
      return
    }

    this.loadClasses()
    this.loadInquiries()
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollbarWidth)
    setTimeout(this.updateScrollbarWidth, 1000)
    
    // Click outside handler for v-select
    this.handleClickOutside = (event) => {
      const clickedElement = event.target
      const isVSelectClick = clickedElement.closest('.v-select') || 
                            clickedElement.closest('.vs__dropdown-menu') ||
                            clickedElement.closest('.vs__dropdown-option') ||
                            clickedElement.closest('.vs__dropdown-toggle') ||
                            clickedElement.closest('.vs__actions') ||
                            clickedElement.closest('.vs__selected-options')
      
      if (!isVSelectClick) {
        const vSelectRefs = ['pageSizeSelect']
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName]?.$el) {
            const toggle = this.$refs[refName].$el.querySelector('.vs__dropdown-toggle')
            if (toggle) toggle.blur()
          }
        })
        
        const allToggles = document.querySelectorAll('.vs__dropdown-toggle')
        allToggles.forEach((toggle) => {
          if (document.activeElement === toggle) toggle.blur()
        })
        
        if (document.activeElement?.classList.contains('vs__dropdown-toggle')) {
          document.activeElement.blur()
        }
      }
    }
    
    document.addEventListener('click', this.handleClickOutside, true)
    document.addEventListener('mousedown', this.handleClickOutside, true)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollbarWidth)
    
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside, true)
      document.removeEventListener('mousedown', this.handleClickOutside, true)
    }
  },
  methods: {
    ...mapActions({ get: 'get', post: 'post', delete: 'delete' }),

    setupRights() {
      let right = {}
      if (this.$store.state.menu) {
        this.$store.state.menu.forEach((el) => {
          if (el.children) {
            el.children.forEach((ch) => {
              right[ch.route] = {
                view: ch.view,
                add: ch.add,
                edit: ch.edit,
                delete: ch.delete,
                assignSections: ch.assignSections,
              }
            })
          }
        })
      }
      this.$store.commit("setRights", right)
      this.rights = this.$store.state.rights[this.$route.name]
    },

    async loadClasses() {
      try {
        const res = await this.get({
          url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&dID=0`,
          token: this.$store.state.userData.token,
        })
        
        let classesArray = []
        const cData = res && res.data ? res.data : res
        
        if (Array.isArray(cData)) {
          classesArray = cData
        } else if (cData && Array.isArray(cData.data)) {
          classesArray = cData.data
        }
        
        this.classesOptions = classesArray.map((el) => el.cls || el)
      } catch (error) {
        console.error('Failed to load classes', error)
        this.$bvToast.toast('Failed to load classes', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async loadInquiries() {
      try {
        this.dataLoading = true

        const response = await this.post({
          url: `${this.$store.state.domain}StudentInquiries/CarnivalLoadApi`,
          body: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            clsID: this.filterClassID || 0,
            lastSchool: this.filterLastSchool,
            id: parseInt(this.filterID) || 0,
            name: this.searchQuery?.trim() || '',
            page: this.currentPage,
            size: this.perPage,
          },
          context: this,
          token: this.$store.state.userData.token,
          allResponse: true,
        })

        let rawData = []
        let totalRecords = 0
        // API body: store returns data.data (full API body) when allResponse: true, so response = { status, data, total, stats, ... }
        const apiBody = response && response.stats ? response : (response && response.data && response.data.stats ? response.data : null)

        if (response) {
          const body = response.data || response
          if (Array.isArray(body)) {
            rawData = body
            totalRecords = response.total ?? response.totalCount ?? body.length
          } else if (body.status === 'success' && Array.isArray(body.data)) {
            rawData = body.data
            totalRecords = body.total ?? body.totalCount ?? body.totalRecords ?? rawData.length
          } else if (Array.isArray(body.data)) {
            rawData = body.data
            totalRecords = body.total ?? body.totalCount ?? body.totalRecords ?? rawData.length
          } else if (body.data && Array.isArray(body.data.data)) {
            rawData = body.data.data
            totalRecords = body.data.total ?? body.data.totalCount ?? rawData.length
          }
        }

        // Always apply stats from API when present (works for both response shapes)
        if (apiBody && apiBody.stats) {
          this.statsData = {
            totalRegistrations: apiBody.stats.totalRegistrations ?? 0,
            totalHeadcount: apiBody.stats.totalHeadcount ?? 0,
            totalPresent: apiBody.stats.totalPresent ?? 0,
            totalChildrenCount: apiBody.stats.totalChildrenCount ?? 0,
            classWise: Array.isArray(apiBody.stats.classWise) ? apiBody.stats.classWise : [],
            schoolWise: Array.isArray(apiBody.stats.schoolWise) ? apiBody.stats.schoolWise : [],
            professionWise: Array.isArray(apiBody.stats.professionWise) ? apiBody.stats.professionWise : [],
          }
        }

        // Map data with class name (API returns { inquiry, checklist, followups } per item)
        this.inquiries = rawData.map((item) => {
          const inquiry = item.inquiry || item
          return {
            ...inquiry,
            className: this.getClassName(inquiry.cls || inquiry.clsID),
          }
        })
        
        this.totalRecords = totalRecords

        this.$nextTick(() => {
          this.updateScrollbarWidth()
        })
      } catch (error) {
        console.error('Failed to load inquiries', error)
        this.$bvToast.toast('Failed to load inquiries', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        this.inquiries = []
        this.totalRecords = 0
        this.statsData = {
          totalRegistrations: 0,
          totalHeadcount: 0,
          totalChildrenCount: 0,
          classWise: [],
          schoolWise: [],
          professionWise: [],
        }
      } finally {
        this.dataLoading = false
      }
    },

    getClassName(id) {
      const obj = this.classesOptions.find((el) => el.id == id)
      return obj ? obj.name : 'N/A'
    },

    async Delete(id) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: this.$t("You won't be able to revert this!"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, Delete it!',
      })

      if (result.isConfirmed) {
        const status = await this.delete({
          url:
            this.$store.state.domain +
            'studentinquiries/Remove?db=' +
            this.$store.state.userData.db +
            '&id=' +
            id,
          message: 'Inquiry deleted successfully.',
          context: this,
          body: null,
          token: this.$store.state.userData.token,
        })
        if (status) this.loadInquiries()
      }
    },

    async SetPresent(id) {
       try {
        const res = await this.post({
          url: `${this.$store.state.domain}StudentInquiries/CarnivalSetStatusPresentApi?db=${this.$store.state.userData.db}&id=${id}`,
          body: {},
          token: this.$store.state.userData.token,
          message: 'Status updated to Present successfully',
          context: this,
        })
        if (res) {
          this.loadInquiries()
        }
      } catch (error) {
        console.error('Failed to update status', error)
      }
    },

    onPageChange(page) {
      this.currentPage = page
      this.loadInquiries()
    },

    onPageSizeChange() {
      this.currentPage = 1
      this.loadInquiries()
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect?.$el) {
          const toggle = this.$refs.pageSizeSelect.$el.querySelector('.vs__dropdown-toggle')
          if (toggle) {
            toggle.blur()
            setTimeout(() => {
              document.body.click()
            }, 100)
          }
        }
      })
    },

    onSearchChange() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      
      // Debounce search to avoid too many API calls
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadInquiries()
      }, 500)
    },

    toggleView(mode) {
      if (this.viewMode === mode) return
      
      this.viewMode = mode
      // Reset filters when switching views
      this.filterID = null
      this.searchQuery = ''
      this.filterClassID = null
      this.filterLastSchool = 'all'
      this.currentPage = 1
      
      this.loadInquiries()
    },

    viewInquiry(item) {
      this.currentInquiry = { ...item }
      this.toggleSidebar(true)
    },

    toggleSidebar(isOpen) {
      this.isSidebarOpen = isOpen
    },

    onSidebarHidden() {
      this.isSidebarOpen = false
      this.currentInquiry = {}
    },

    onSidebarChange(val) {
      this.isSidebarOpen = val
    },

    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      if (isNaN(d.getTime())) return 'N/A'
      
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    },

    cleanNoteText(text) {
      if (!text) return 'N/A'
      // Remove "Children:" prefix case-insensitively
      return text.replace(/^Children:\s*/i, '')
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper
          const topScrollbar = this.$refs.topScrollbar
          const contentElement = topScrollbar.querySelector('.table-scrollbar-content')
          
          if (contentElement && tableWrapper.scrollWidth > tableWrapper.clientWidth) {
            contentElement.style.width = `${tableWrapper.scrollWidth}px`
            
            // Fix: Add listener for top to bottom sync
            topScrollbar.onscroll = () => {
              tableWrapper.scrollLeft = topScrollbar.scrollLeft
            }
          }
        }
      })
    },

    syncScroll(event) {
      if (this.$refs.topScrollbar && event.target === this.$refs.tableWrapper) {
        this.$refs.topScrollbar.scrollLeft = event.target.scrollLeft
      }
    },

    async exportToExcel() {
      try {
        this.exportLoading = true

        // Fetch all records without pagination
        const response = await this.post({
          url: `${this.$store.state.domain}StudentInquiries/CarnivalLoadApi`,
          body: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            clsID: this.filterClassID || 0,
            lastSchool: this.filterLastSchool,
            id: parseInt(this.filterID) || 0,
            name: this.searchQuery?.trim() || '',
            page: 1,
            size: 999999, // Get all records
          },
          context: this,
          token: this.$store.state.userData.token,
          allResponse: true,
        })

        let rawData = []
        if (response) {
          const body = response.data || response
          if (Array.isArray(body)) {
            rawData = body
          } else if (body.status === 'success' && Array.isArray(body.data)) {
            rawData = body.data
          } else if (Array.isArray(body.data)) {
            rawData = body.data
          } else if (body.data && Array.isArray(body.data.data)) {
            rawData = body.data.data
          }
        }

        // Map data to match the table view columns
        const exportData = rawData.map((item, index) => {
          const inquiry = item.inquiry || item
          return {
            'S.No': index + 1,
            'Code': inquiry.id || '',
            'Full Name': inquiry.name || 'N/A',
            'Class': this.getClassName(inquiry.cls || inquiry.clsID),
            'Head Count': inquiry.age || '0',
            'Contact': inquiry.contact || 'N/A',
            'Email': inquiry.email || 'N/A',
            'CNIC': inquiry.cnic || 'N/A',
            'Last School': inquiry.lastSchool || 'N/A',
            'Note': this.cleanNoteText(inquiry.note),
            'Profession': inquiry.finalRemarks || 'N/A',
            'Status': (inquiry.inquiryStatus || '').toLowerCase() === 'present' ? 'Present' : 'Pending',
            'Date': this.formatDate(inquiry.date),
          }
        })

        if (exportData.length === 0) {
          this.$bvToast.toast('No data to export', {
            title: 'Warning',
            variant: 'warning',
            solid: true,
          })
          return
        }

        // Create worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(exportData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Carnival Inquiries')

        // Generate filename with current date
        const date = new Date()
        const dateStr = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
        const filename = `Carnival_Inquiries_${dateStr}.xlsx`

        // Save file
        XLSX.writeFile(workbook, filename)

        this.$bvToast.toast('Data exported successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Failed to export data', error)
        this.$bvToast.toast('Failed to export data', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.exportLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

/* Resources Card Styling */
.resources-list-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: none;
  overflow: visible;
}

/* Full-Width Gradient Line */
.gradient-line-wrapper {
  height: 4px;
  background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  border-radius: 12px 12px 0 0;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
}

/* Resource Title */
.resource-title {
  font-size: 1.285rem;
  font-weight: 700;
  color: #5e5873;
  letter-spacing: -0.02em;
}

/* Dashboard Section Styles */
.dashboard-section {
  background: transparent;
}

.metric-card {
  padding: 0.85rem 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  .metric-icon-wrapper {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.4);
    box-shadow: inset 0 0 12px rgba(255, 255, 255, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1);
    color: white;
    flex-shrink: 0;
    
    ::v-deep svg {
      filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
      stroke-width: 2;
    }
  }
  
  .metric-label {
    font-size: 0.7rem;
    font-weight: 600;
    opacity: 0.9;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
  
  .metric-value {
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.1;
  }
}

.gold-gradient { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.indigo-gradient { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); }
.emerald-gradient { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.rose-gradient { background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%); }
.amber-gradient { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); }

.ring-gold { border: 2px solid rgba(255, 255, 255, 0.4); }
.ring-indigo { border: 2px solid rgba(255, 255, 255, 0.4); }
.ring-emerald { border: 2px solid rgba(255, 255, 255, 0.4); }
.ring-rose { border: 2px solid rgba(255, 255, 255, 0.4); }
.ring-amber { border: 2px solid rgba(255, 255, 255, 0.4); }


/* Modern Chart Cards */
.modern-chart-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

.chart-header-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.chart-title-modern {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chart-content-modern {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.text-indigo {
  color: #6366f1 !important;
}

.chart-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  
  .chart-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Quality Alert */
.quality-alert {
  background: #fff;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(255, 159, 67, 0.1);
  
  .alert-gradient-border {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    background: linear-gradient(180deg, #ff9f43 0%, #ff6b6b 100%);
  }
  
  .alert-icon-wrapper {
    width: 42px;
    height: 42px;
    background: #fff7ed;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Modern Filters */
.filter-header-modern {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}

.input-modern-group {
  position: relative;
  display: flex;
  align-items: center;
  
  .input-icon {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    z-index: 5;
  }
  
  .filter-control-modern {
    padding-left: 38px;
    height: 42px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.1);
    }
  }
  
  .v-select-modern {
    width: 100%;
    
    ::v-deep .vs__dropdown-toggle {
      padding-left: 32px;
      height: 42px;
      border-radius: 10px;
      border: 1.5px solid #e2e8f0;
      background: white;
    }
  }
}

.refresh-btn {
  border-radius: 10px;
  font-weight: 600;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

/* Pagination Controls */
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    
    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
  
  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    
    &:hover {
      border-color: #b8a9ff !important;
    }
    
    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }
  
  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }
  
  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
  
  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;
    
    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }
    
    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }
    
    &:active {
      background-color: #e5e7eb !important;
    }
    
    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;
  
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
  
  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  
  ::v-deep .pagination {
    margin-bottom: 0;
    
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }
    
    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
    
    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

/* Custom Status Pills */
.status-pill {
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-width: 85px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.status-present {
    background: #ffffff;
    border: 1.5px solid #28c76f;
    color: #28c76f;
  }
  
  &.status-pending {
    background: #ffffff;
    border: 1.5px solid #ff9f43;
    color: #ff9f43;
  }
}

/* Action Button Styling */
.btn-icon.rounded-circle {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table Styling */
.table-scroll-container {
  position: relative;
  margin-top: 1rem;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  height: 15px;
  margin-bottom: 0.5rem;
  border-radius: 4px;
  background: #f8fafc;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.table-scrollbar-content {
  height: 1px;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.resources-table {
  margin-bottom: 0;
  
  ::v-deep {
    thead th {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      color: #64748b;
      padding: 1rem;
      white-space: nowrap;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    tbody tr {
      transition: all 0.2s ease;
      cursor: pointer;
      border-left: 2px solid transparent;
      
      &:hover {
        background-color: #f8fafc;
        border-left-color: #7367f0;
      }
      
      td {
        padding: 1rem;
        vertical-align: middle;
        border-bottom: 1px solid #f1f5f9;
        font-size: 0.9rem;
        color: #64748b;
      }
    }
  }
}

.custom-badge-modern {
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
}

.avatar-wrapper {
  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Skeleton Loader */
.skeleton-row {
  td.skeleton-cell {
    padding: 0.875rem 1rem;
    
    .skeleton-loader {
      height: 20px;
      background: linear-gradient(
        90deg,
        #f0f0f0 0%,
        #e0e0e0 20%,
        #f0f0f0 40%,
        #f0f0f0 100%
      );
      background-size: 200% 100%;
      border-radius: 4px;
      animation: shimmer 1.5s infinite;
    }
  }
}

/* View Toggle Styling */
.custom-toggle-group {
  display: flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.toggle-btn {
  padding: 8px 16px;
  border-radius: 9px;
  border: none;
  background: transparent;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  
  &:hover {
    color: #7367f0;
    background: rgba(115, 103, 240, 0.05);
  }
  
  &.active {
    background: #ffffff;
    color: #7367f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}

.view-toggle-wrapper {
  flex-shrink: 0;
}

/* Rest of styles */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.skeleton-pulse {
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

.skeleton-shimmer {
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e8e8e8 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes skeletonPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Sidebar Styling */
.resource-sidebar {
  ::v-deep .b-sidebar {
    .b-sidebar-body {
      padding: 0;
    }
  }
}

.sidebar-header-gradient {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2);
}

.sidebar-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sidebar-icon-wrapper {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.sidebar-title-icon {
  color: #fff;
}

.sidebar-title {
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;
}

.sidebar-close-btn-modern {
  background: transparent;
  border: none;
  color: #fff !important;
  padding: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff !important;
  }
  
  &:focus {
    box-shadow: none;
  }
}

.sidebar-body-modern {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1.5rem;
    
    ::v-deep label {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.5rem;
      font-size: 0.95rem;
    }
  }
  
  .modern-display-text {
    padding: 0.75rem;
    background: #f8f8f8;
    border-radius: 8px;
    border: 1px solid #ebe9f1;
    font-size: 0.95rem;
    color: #6e6b7b;
    min-height: 42px;
    display: flex;
    align-items: center;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .pagination-controls-top {
    flex-direction: column;
    gap: 1rem;
    
    .pagination-count-display,
    .pagination-wrapper-compact {
      width: 100%;
      text-align: center;
    }
  }
  
  .table-scrollbar-top {
    display: none;
  }
}

/* V-Select Customization */
::v-deep .v-select {
  .vs__dropdown-toggle {
    border-radius: 8px;
    border: 1px solid #d8d6de;
    padding: 0.371rem 0.5rem;
  }
  
  .vs__dropdown-menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #ebe9f1;
  }
  
  .vs__dropdown-option--highlight {
    background: #7367f0;
    color: #fff;
  }
}
</style>
